import { useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

import type { MediaSource, Quiz } from '@/entities/course/model/dto';
import { useAudioStore } from '@/shared/state/audio-store';

const isHlsSource = (mimeType: string) =>
  mimeType === 'application/x-mpegURL' ||
  mimeType === 'application/vnd.apple.mpegurl';

const isDashSource = (mimeType: string) => mimeType === 'application/dash+xml';

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export interface QuizPlayerState {
  isReady: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  isEnded: boolean;
  currentTime: number;
  duration: number;
  progressPercent: number;
}

const createInitialState = (): QuizPlayerState => ({
  isReady: false,
  isPlaying: false,
  isMuted: false,
  isEnded: false,
  currentTime: 0,
  duration: 0,
  progressPercent: 0,
});

export const pickPreferredMediaSource = (
  sources?: MediaSource[],
): MediaSource | null => {
  if (!sources?.length) return null;

  const preferred =
    sources.find((source) => isHlsSource(source.mime_type)) ??
    sources.find((source) => isDashSource(source.mime_type)) ??
    sources.find((source) => source.mime_type.startsWith('audio/'));

  return preferred ?? sources[0]!;
};

interface QuizPlaylistItem {
  quizId: string;
  quiz: Quiz;
  source: MediaSource | null;
}

interface UseQuizAudioPlayerOptions {
  quizzes: Quiz[];
  activeQuizId?: string;
  onRequestQuizChange?: (quizId: string) => void;
}

export const useQuizAudioPlayer = ({
  quizzes,
  activeQuizId,
  onRequestQuizChange,
}: UseQuizAudioPlayerOptions) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const dashRef = useRef<import('dashjs').MediaPlayerClass | null>(null);
  const shouldAutoplayRef = useRef(false);

  const globalMuted = useAudioStore((state) => state.isMuted);
  const setGlobalMuted = useAudioStore((state) => state.setMuted);

  const [state, setState] = useState<QuizPlayerState>(createInitialState);
  const playlist: QuizPlaylistItem[] = quizzes.map((quizItem) => ({
    quizId: quizItem.id,
    quiz: quizItem,
    source: pickPreferredMediaSource(quizItem.media_sources),
  }));

  const activeQuiz: Quiz | null = (() => {
    if (!quizzes.length || !activeQuizId) return null;
    return quizzes.find((quizItem) => quizItem.id === activeQuizId) ?? null;
  })();

  const activePlaylistItem: QuizPlaylistItem | null = activeQuiz
    ? playlist.find((item) => item.quizId === activeQuiz.id) ?? null
    : null;

  const currentIndex = activePlaylistItem
    ? playlist.findIndex((item) => item.quizId === activePlaylistItem.quizId)
    : -1;

  const nextPlaylistItem = currentIndex < 0 ? null : playlist[currentIndex + 1] ?? null;

  const previousPlaylistItem =
    currentIndex <= 0 ? null : playlist[currentIndex - 1] ?? null;

  const mediaSource = activePlaylistItem?.source ?? null;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const savedPosition =
      activeQuiz?.user_content_quiz_association?.play_position_secs ?? 0;

    shouldAutoplayRef.current = true;

    setState((prev) => ({
      ...prev,
      isReady: false,
      isEnded: false,
      isPlaying: false,
      currentTime: savedPosition,
      duration: activeQuiz?.duration_secs ?? prev.duration ?? 0,
      progressPercent: savedPosition
        ? clamp(
            (savedPosition / (activeQuiz?.duration_secs ?? 1)) * 100,
            0,
            100,
          )
        : 0,
    }));

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (dashRef.current) {
      dashRef.current.reset();
      dashRef.current = null;
    }

    audio.pause();
    audio.removeAttribute('src');
    audio.load();

    if (!activeQuiz || !mediaSource) {
      return;
    }

    const initialisePlayback = async () => {
      if (typeof window === 'undefined') return;

      if (isHlsSource(mediaSource.mime_type)) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hlsRef.current = hls;
          hls.loadSource(mediaSource.url);
          hls.attachMedia(audio);
        } else {
          audio.src = mediaSource.url;
          audio.load();
        }
      } else if (isDashSource(mediaSource.mime_type)) {
        const dashModule = (await import('dashjs')) as typeof import('dashjs');
        const dashPlayer = dashModule.MediaPlayer().create();
        dashPlayer.initialize(audio, mediaSource.url, false);
        dashRef.current = dashPlayer;
      } else {
        audio.src = mediaSource.url;
        audio.load();
      }
    };

    void initialisePlayback();

    const handleMetadata = () => {
      if (savedPosition > 0) {
        try {
          audio.currentTime = savedPosition;
        } catch {
          // ignore seek errors
        }
      }
    };

    audio.addEventListener('loadedmetadata', handleMetadata, { once: true });

    return () => {
      audio.removeEventListener('loadedmetadata', handleMetadata);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (dashRef.current) {
        dashRef.current.reset();
        dashRef.current = null;
      }
    };
  }, [
    activeQuiz?.duration_secs,
    activeQuiz?.id,
    activeQuiz?.user_content_quiz_association?.play_position_secs,
    mediaSource?.mime_type,
    mediaSource?.url,
  ]);

  // Sync audio element muted state with global store (without triggering reload)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = globalMuted;
    setState((prev) => ({
      ...prev,
      isMuted: globalMuted,
    }));
  }, [globalMuted]);

  const audioElement = audioRef.current;

  useEffect(() => {
    const audio = audioElement;
    if (!audio) return;

    const handlePlay = () =>
      setState((prev) => ({
        ...prev,
        isPlaying: true,
        isEnded: false,
      }));

    const handlePause = () =>
      setState((prev) => ({
        ...prev,
        isPlaying: false,
      }));

    const handleTimeUpdate = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      const currentTime = audio.currentTime || 0;
      const progressPercent =
        duration > 0 ? clamp((currentTime / duration) * 100, 0, 100) : 0;

      setState((prev) => ({
        ...prev,
        currentTime,
        duration: duration || prev.duration,
        progressPercent,
      }));
    };

    const handleLoadedMetadata = () =>
      setState((prev) => ({
        ...prev,
        duration: Number.isFinite(audio.duration)
          ? audio.duration
          : prev.duration,
      }));

    const handleCanPlay = () => {
      setState((prev) => ({
        ...prev,
        isReady: true,
      }));

      if (shouldAutoplayRef.current) {
        shouldAutoplayRef.current = false;
        void audio.play().catch(() => undefined);
      }
    };

    const handleEnded = () =>
      setState((prev) => ({
        ...prev,
        isEnded: true,
        isPlaying: false,
        progressPercent: 100,
      }));

    const handleVolumeChange = () =>
      setState((prev) => ({
        ...prev,
        isMuted: audio.muted,
      }));

    const handleWaiting = () =>
      setState((prev) => ({
        ...prev,
        isReady: false,
      }));

    const handlePlaying = () =>
      setState((prev) => ({
        ...prev,
        isReady: true,
      }));

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);

    return () => {
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [audioElement]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    shouldAutoplayRef.current = false;
    setState((prev) => ({
      ...prev,
      isPlaying: true,
      isEnded: false,
    }));

    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      void playPromise.catch(() => {
        setState((prev) => ({
          ...prev,
          isPlaying: false,
        }));
      });
    }
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    shouldAutoplayRef.current = false;
    audio.pause();
    setState((prev) => ({
      ...prev,
      isPlaying: false,
    }));
  }, []);

  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const nextTime = clamp(time, 0, duration || time);

    try {
      audio.currentTime = nextTime;
      setState((prev) => ({
        ...prev,
        currentTime: nextTime,
        progressPercent:
          duration > 0 ? clamp((nextTime / duration) * 100, 0, 100) : 0,
      }));
    } catch {
      // ignore seek errors
    }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newMutedState = !audio.muted;
    audio.muted = newMutedState;
    setGlobalMuted(newMutedState);
    setState((prev) => ({
      ...prev,
      isMuted: newMutedState,
    }));
  }, [setGlobalMuted]);

  const restart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      shouldAutoplayRef.current = false;
      setState((prev) => ({
        ...prev,
        isPlaying: true,
        isEnded: false,
        currentTime: 0,
        progressPercent: 0,
      }));

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        void playPromise.catch(() => {
          setState((prev) => ({
            ...prev,
            isPlaying: false,
          }));
        });
      }
    } catch {
      // ignore errors
    }
  }, []);

  const playNextQuiz = useCallback(() => {
    if (!nextPlaylistItem?.quizId) return;
    onRequestQuizChange?.(nextPlaylistItem.quizId);
  }, [nextPlaylistItem?.quizId, onRequestQuizChange]);

  const playPreviousQuiz = useCallback(() => {
    if (!previousPlaylistItem?.quizId) return;
    onRequestQuizChange?.(previousPlaylistItem.quizId);
  }, [onRequestQuizChange, previousPlaylistItem?.quizId]);

  return {
    audioRef,
    state,
    hasSource: Boolean(mediaSource),
    play,
    pause,
    seekTo,
    toggleMute,
    restart,
    playlist,
    currentQuizId: activeQuiz?.id ?? null,
    nextQuizId: nextPlaylistItem?.quizId ?? null,
    previousQuizId: previousPlaylistItem?.quizId ?? null,
    playNextQuiz,
    playPreviousQuiz,
  };
};
